/**
 * SKU Projection Grid Tab — Consolidated Forecast Table
 * Shows all forecasts across stores and SKUs in a flat table.
 * Supports server-side pagination, sorting, and Excel export.
 * Consumes global filters from FilterContext.
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, Chip, TablePagination, IconButton, Tooltip,
  TextField,
} from '@mui/material';
import {
  GridOn, Refresh,
  ArrowUpward, ArrowDownward,
  UnfoldLess, UnfoldMore,
  Fullscreen, FullscreenExit,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer, Line, ComposedChart,
} from 'recharts';
import api from '../../services/api';
import { useForecastingFilters } from './ForecastingFilterContext';
import { exportWithChart } from '../../utils/exportUtils';
import ExportMenu from '../Common/ExportMenu';

/* Sticky column definitions — compact widths to show 6+ months without scroll */
const STICKY_COLS = [
  { key: 'store_name', label: 'Store', width: 110, sortable: true, collapsible: false },
  { key: 'brand', label: 'Brand', width: 72, sortable: true, collapsible: true },
  { key: 'sku', label: 'SKU', width: 90, sortable: true, collapsible: false },
  { key: 'product_name', label: 'Product', width: 120, sortable: true, collapsible: false },
  { key: 'category', label: 'Cat.', width: 62, sortable: true, collapsible: true },
  { key: 'product_family', label: 'Family', width: 80, sortable: true, collapsible: true },
  { key: 'lifecycle_stage', label: 'Item Status', width: 85, sortable: true, collapsible: true },
  { key: 'abc_classification', label: 'ABC', width: 40, sortable: true, collapsible: true },
  { key: 'average_sales_6m', label: 'Avg 6M', width: 58, sortable: true, collapsible: true },
];

const getVisibleCols = (expanded: boolean) => {
  const cols = expanded ? STICKY_COLS : STICKY_COLS.filter((c) => !c.collapsible);
  let left = 0;
  return cols.map((col) => {
    const entry = { ...col, left };
    left += col.width;
    return entry;
  });
};

const statusColor: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  NEW: 'info',
  GROWTH: 'success',
  MATURE: 'default',
  DECLINING: 'warning',
  PHASE_OUT: 'error',
  DISCONTINUED: 'error',
};

const ProjectionGridTab: React.FC = () => {
  const { brands, categories, stores: selectedStoreIds, cities } = useForecastingFilters();
  const [months, setMonths] = useState(18);
  const [startMonth, setStartMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortBy, setSortBy] = useState('store_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [shouldLoad, setShouldLoad] = useState(false);
  const [colsExpanded, setColsExpanded] = useState(true);
  const visibleCols = useMemo(() => getVisibleCols(colsExpanded), [colsExpanded]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  /* Fullscreen ESC handler */
  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsFullscreen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isFullscreen]);

  const { data: projectionData, isLoading, error, refetch } = useQuery({
    queryKey: ['projection', brands, categories, selectedStoreIds, cities, months, startMonth, page, rowsPerPage, sortBy, sortDir],
    queryFn: async () => {
      const response = await api.getForecastProjection(
        undefined, // product_id
        selectedStoreIds.length === 1 ? selectedStoreIds[0] : undefined,
        months,
        {
          brand: brands.length > 0 ? brands.join(',') : undefined,
          category: categories.length > 0 ? categories.join(',') : undefined,
          city: cities.length > 0 ? cities.join(',') : undefined,
          start_month: startMonth || undefined,
          page: page + 1,
          page_size: rowsPerPage,
          sort_by: sortBy,
          sort_dir: sortDir,
        }
      );
      return response.data;
    },
    enabled: shouldLoad,
  });

  const handleLoadProjection = () => {
    setShouldLoad(true);
    refetch();
  };

  const handleSort = useCallback((column: string) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  }, [sortBy]);

  const monthHeaders: string[] = projectionData?.month_headers || [];
  const dataRows: any[] = projectionData?.rows || [];
  const totalRows = projectionData?.total_rows || 0;

  /* Flatten current-page rows for CSV / PDF export via <ExportMenu>. */
  const flattenRows = useCallback((rawRows: any[], headers: string[]) =>
    rawRows.map((row: any) => {
      const obj: Record<string, any> = {
        'Store': row.store_name,
        'SKU': row.sku,
        'Product Description': row.product_name,
        'Brand': row.brand || '',
        'Category': row.category || '',
        'Range / Product Family': row.product_family || '',
        'Item Status': row.lifecycle_stage || '',
        'ABC Classification': row.abc_classification || '',
        'Avg Sales (6M)': Math.round(row.average_sales_6m ?? 0),
      };
      headers.forEach((h: string) => {
        const forecast = row.monthly_forecast?.[h] ?? 0;
        const historical = row.monthly_historical?.[h] ?? 0;
        const variance = forecast - historical;
        const variancePct = historical > 0 ? ((variance / historical) * 100) : 0;
        obj[`${h} Forecast`] = forecast;
        obj[`${h} Historical`] = historical;
        obj[`${h} Variance`] = variance;
        obj[`${h} Variance %`] = `${variancePct.toFixed(1)}%`;
      });
      return obj;
    }), []);

  const exportRows = useMemo(
    () => flattenRows(dataRows, monthHeaders),
    [dataRows, monthHeaders, flattenRows],
  );

  /**
   * Excel export delegates to a custom handler so the full (all-pages) dataset
   * is fetched before writing the workbook. CSV/PDF use the in-memory current
   * page (trade-off noted).
   */
  const handleExcelExport = async () => {
    if (!projectionData?.rows) return;

    let allRows: any[] = projectionData.rows;
    let allHeaders: string[] = projectionData.month_headers || [];
    const total = projectionData.total_rows || allRows.length;

    if (total > allRows.length) {
      try {
        const exportResp = await api.getForecastProjection(
          undefined,
          selectedStoreIds.length === 1 ? selectedStoreIds[0] : undefined,
          months,
          {
            brand: brands.length > 0 ? brands.join(',') : undefined,
            category: categories.length > 0 ? categories.join(',') : undefined,
            city: cities.length > 0 ? cities.join(',') : undefined,
            page: 1,
            page_size: 10000,
            sort_by: sortBy,
            sort_dir: sortDir,
          },
        );
        allRows = exportResp.data?.rows || allRows;
        allHeaders = exportResp.data?.month_headers || allHeaders;
      } catch {
        /* Fallback: export current page only */
      }
    }

    const rows = flattenRows(allRows, allHeaders);
    const dateStr = new Date().toISOString().slice(0, 10);
    await exportWithChart(
      rows,
      `IRIS_Forecast_Export_${dateStr}`,
      'Forecast Projection',
      chartContainerRef,
      'Forecast Data',
    );
  };

  /* Chart data: aggregated monthly forecast & historical totals (from backend, across all rows) */
  const chartData = React.useMemo(() => {
    const totals = projectionData?.monthly_totals;
    const histTotals = projectionData?.monthly_historical_totals;
    if (!totals || monthHeaders.length === 0) return [];
    return monthHeaders.map((h) => ({
      month: h,
      forecast: totals[h] || 0,
      historical: histTotals?.[h] || 0,
    }));
  }, [projectionData, monthHeaders]);

  const cellValue = (row: any, key: string) => {
    switch (key) {
      case 'average_sales_6m':
        // Average product quantity — show as a rounded integer, no decimals.
        return row.average_sales_6m != null
          ? Math.round(row.average_sales_6m).toLocaleString()
          : '—';
      default: return row[key] || '—';
    }
  };

  const tableContent = (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
          Consolidated Forecast Table
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {projectionData && dataRows.length > 0 && (
            <>
              <ExportMenu
                data={exportRows}
                filename={`IRIS_Forecast_Export_${new Date().toISOString().slice(0, 10)}`}
                title="Forecast Projection"
                sheetName="Forecast Data"
                chartRef={chartContainerRef}
                customExcelHandler={handleExcelExport}
              />
              <Tooltip title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen'}>
                <IconButton size="small" onClick={() => setIsFullscreen((v) => !v)}>
                  {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Consolidated sales forecast by store and SKU. Uses global filters above.
        Click column headers to sort. Click "Generate Projection" to load data.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{(error as any)?.response?.data?.detail || 'Failed to load projection'}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            size="small"
            type="month"
            label="Starting Month"
            value={startMonth}
            onChange={(e) => setStartMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Months"
            value={months}
            onChange={(e) => setMonths(Math.max(6, Math.min(24, parseInt(e.target.value) || 18)))}
            inputProps={{ min: 6, max: 24 }}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
            onClick={handleLoadProjection}
            disabled={isLoading}
            fullWidth
            sx={{ height: '40px' }}
          >
            {isLoading ? 'Generating...' : 'Generate Projection'}
          </Button>
        </Grid>
      </Grid>

      {/* Monthly Forecast Chart */}
      {projectionData && chartData.length > 0 && (
        <Paper ref={chartContainerRef} sx={{ p: '20px', border: '1px solid var(--color-border)', mb: 3 }}>
          <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>
            Monthly Forecast Value
          </Typography>
          <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
            Aggregated forecast quantities across all {totalRows} SKU × Store combinations
            {' · '}
            <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#722F37', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> Forecast
            {' · '}
            <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#C9A96E', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> Historical (same month last year)
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartData} margin={{ left: 8, right: 8, top: 10 }} barCategoryGap="18%">
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fill: '#8A8A85' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E5E0' }}
                angle={-35}
                textAnchor="end"
                height={50}
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#8A8A85' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) =>
                  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M`
                    : v >= 1_000 ? `${(v / 1_000).toFixed(0)}K`
                    : `${v}`
                }
              />
              <RTooltip
                formatter={(value: number, name: string) => [
                  value.toLocaleString() + ' units',
                  name === 'forecast' ? 'Forecast' : 'Historical',
                ]}
                contentStyle={{
                  background: '#FDFBF7',
                  border: '1px solid #E8E0D4',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(114,47,55,0.08)',
                  fontSize: '12px',
                  padding: '10px 14px',
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '11px' }}
                formatter={(val: string) => val === 'forecast' ? 'Forecast' : 'Historical'}
              />
              <Bar dataKey="forecast" fill="#722F37" name="forecast" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="historical" stroke="#C9A96E" strokeWidth={2} dot={{ r: 3, fill: '#C9A96E' }} name="historical" />
            </ComposedChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {projectionData && dataRows.length > 0 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {totalRows} row{totalRows > 1 ? 's' : ''} — {months} months projection
              </Typography>
              <Tooltip title={colsExpanded ? 'Collapse detail columns' : 'Expand detail columns'}>
                <IconButton size="small" onClick={() => setColsExpanded((v) => !v)} sx={{ color: 'var(--color-text-muted)' }}>
                  {colsExpanded ? <UnfoldLess sx={{ fontSize: 18 }} /> : <UnfoldMore sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            </Box>
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '12px' } }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: isFullscreen ? 'calc(100vh - 200px)' : 'calc(100vh - 380px)', overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {visibleCols.map((col, i) => (
                    <TableCell
                      key={col.key}
                      onClick={() => col.sortable && handleSort(col.key)}
                      sx={{
                        fontWeight: 700, fontSize: '10px', position: 'sticky', left: col.left,
                        bgcolor: 'var(--color-surface)', zIndex: 3, minWidth: col.width, maxWidth: col.width,
                        borderRight: i === visibleCols.length - 1
                          ? '2px solid var(--color-border)'
                          : '1px solid var(--color-border)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        px: '6px',
                        cursor: col.sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                        '&:hover': col.sortable ? { bgcolor: 'var(--color-bg-secondary)' } : {},
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {col.label}
                        {sortBy === col.key && (
                          sortDir === 'asc'
                            ? <ArrowUpward sx={{ fontSize: 12, color: 'var(--color-accent)' }} />
                            : <ArrowDownward sx={{ fontSize: 12, color: 'var(--color-accent)' }} />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                  {monthHeaders.map((header) => {
                    // header format: "May 2026" → split into month + year
                    const parts = header.split(' ');
                    const monthName = parts[0] || header;
                    const year = parts[1] || '';
                    return (
                      <TableCell
                        key={header}
                        align="right"
                        sx={{ fontWeight: 700, fontSize: '10px', minWidth: 48, maxWidth: 56, px: '4px', lineHeight: 1.2 }}
                      >
                        {monthName}<br /><span style={{ fontWeight: 500, fontSize: '9px', color: 'var(--color-text-muted)' }}>{year}</span>
                      </TableCell>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontSize: '10px', minWidth: 52, borderLeft: '2px solid var(--color-border)', px: '4px' }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataRows.map((row: any, idx: number) => {
                  const total = monthHeaders.reduce((sum: number, h: string) => sum + (row.monthly_forecast?.[h] || 0), 0);
                  const rowBg = idx % 2 === 0 ? 'var(--color-surface)' : 'var(--color-bg-secondary)';
                  return (
                    <TableRow key={`${row.store_id}-${row.product_id}`} hover>
                      {visibleCols.map((col, i) => (
                        <TableCell
                          key={col.key}
                          sx={{
                            fontSize: '11px', position: 'sticky', left: col.left,
                            bgcolor: rowBg, zIndex: 1,
                            borderRight: i === visibleCols.length - 1
                              ? '2px solid var(--color-border)'
                              : '1px solid var(--color-border)',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            minWidth: col.width, maxWidth: col.width, px: '6px',
                            ...(col.key === 'sku' ? { fontFamily: 'monospace', fontSize: '10px' } : {}),
                          }}
                        >
                          {col.key === 'lifecycle_stage' && row.lifecycle_stage ? (
                            <Chip
                              label={row.lifecycle_stage}
                              size="small"
                              color={statusColor[row.lifecycle_stage] || 'default'}
                              variant="outlined"
                              sx={{ fontSize: '10px', height: 20 }}
                            />
                          ) : col.key === 'abc_classification' && row.abc_classification ? (
                            <Chip
                              label={row.abc_classification}
                              size="small"
                              variant="filled"
                              sx={{
                                fontSize: '11px', height: 20, fontWeight: 700,
                                bgcolor: row.abc_classification === 'A' ? '#e8f5e9'
                                  : row.abc_classification === 'B' ? '#e3f2fd'
                                  : row.abc_classification === 'C' ? '#fff3e0'
                                  : row.abc_classification === 'D' ? '#fce4ec'
                                  : row.abc_classification === 'E' ? '#f5f5f5'
                                  : '#f5f5f5',
                                color: row.abc_classification === 'A' ? '#2e7d32'
                                  : row.abc_classification === 'B' ? '#1565c0'
                                  : row.abc_classification === 'C' ? '#e65100'
                                  : row.abc_classification === 'D' ? '#c62828'
                                  : row.abc_classification === 'E' ? '#616161'
                                  : '#616161',
                              }}
                            />
                          ) : (
                            cellValue(row, col.key)
                          )}
                        </TableCell>
                      ))}
                      {monthHeaders.map((header) => (
                        <TableCell key={header} align="right" sx={{ fontSize: '11px', fontVariantNumeric: 'tabular-nums', px: '4px' }}>
                          {(row.monthly_forecast?.[header] || 0).toLocaleString()}
                        </TableCell>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                          borderLeft: '2px solid var(--color-border)', px: '4px',
                        }}
                      >
                        {total.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {projectionData && dataRows.length === 0 && !isLoading && (
        <Alert severity="info">No projection data available. Ensure sales history exists for the selected filters.</Alert>
      )}

      {!shouldLoad && !isLoading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <GridOn sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">
            Click "Generate Projection" to view the consolidated monthly forecast grid.
          </Typography>
        </Paper>
      )}
    </>
  );

  return (
    <Box>
      {isFullscreen ? (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, bgcolor: 'var(--color-surface)', p: 3, overflow: 'auto' }}>
          {tableContent}
        </Box>
      ) : (
        tableContent
      )}
    </Box>
  );
};

export default ProjectionGridTab;
